import * as React from 'react';
import { useEffect } from 'react';
import { graphql } from 'react-apollo';

import { DegreePageHeader } from '@coursera-web/composable-page-header-degree';
import { flowRight } from 'lodash';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import redirect from 'js/lib/coursera.redirect';
import { getCmsLocaleString } from 'js/lib/language';
import mapProps from 'js/lib/mapProps';
import Retracked from 'js/lib/retracked';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';

import { browserTabOrWindowUniqueIdentifier } from 'bundles/admin-shared/analytics/WithHeartbeat/lib';
import { getGrowthCmsGraphqlContext } from 'bundles/cms/utils/CmsUtils';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import type {
  DegreeHomePageContentQuery,
  DegreeHomePageContentQueryVariables,
} from 'bundles/degree-home/__generated__/contentfulTypes';
import IntroModalWrapper from 'bundles/degree-home/components/IntroModalWrapper';
import TabsContainer from 'bundles/degree-home/components/TabsContainer';
import type { DegreesV1 } from 'bundles/degree-home/components/__generated__/DegreesV1';
import CoursesContextProvider from 'bundles/degree-home/components/v2/CoursesProvider';
import { DegreeContextProvider } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import type { Partner } from 'bundles/degree-home/types/Degree';
import {
  convertPremiumCertificatePageContentToGenericPageContent,
  convertUniversityProgramPageContentToGenericPageContent,
} from 'bundles/degree-home/types/contentfulTypes';
import type { PageContent } from 'bundles/degree-home/types/contentfulTypes';
import { degreeHomePageContentQuery } from 'bundles/degree-home/utils/DegreeHomeAppContentfulQuery';
import { initializePendo } from 'bundles/degree-home/utils/initializePendo';
import type { LoadedMembershipStatus } from 'bundles/degree-home/utils/useDegreeMembershipStatus';
import withNaptimeAsProp from 'bundles/degree-home/utils/withNaptimeAsProp';
import type { UserAgent } from 'bundles/item-lti/constants/types';
import type Naptime from 'bundles/naptimejs';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';
import TimezoneMismatchNotification from 'bundles/page/components/TimezoneMismatchNotification';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import PrivacyWarningBanner from 'bundles/user-consent/components/PrivacyWarningBanner';
import withMarketingConsent from 'bundles/user-consent/components/withMarketingConsent';
import { shouldShowToUBanner } from 'bundles/user-consent/utils/shouldShowBannerToU';

import 'css!./../__styl__/DegreeHomeApp';

import { EnrollmentDialogContainer } from '../EnrollmentDialogContainer';
import DegreeHomeHeader from './DegreeHomeHeader';

type InputProps = {
  userId: string;
  location: InjectedRouter['location'];
  degreeSlug: string;
  degreeMetadata: DegreesV1 | null | undefined;
  membershipStatus: LoadedMembershipStatus;
  partners: Array<Partner>;
  isDPMEnabled: boolean;
};

type PropsFromStore = {
  userAgent: UserAgent;
};

export type Props = InputProps &
  Naptime.AddedProps &
  PropsFromStore & {
    children: JSX.Element;
    metatagTitle: string;
    degreeMetadata: DegreesV1;
    pageContent?: PageContent | null;
  };

export const DegreeHomeAppV2 = (props: Props) => {
  const [initialized, setInitialized] = React.useState(false);

  const {
    naptime,
    userId,
    membershipStatus,
    degreeSlug,
    degreeMetadata,
    pageContent,
    partners,
    userAgent,
    children,
    metatagTitle,
    isDPMEnabled,
  } = props;

  useEffect(
    () => {
      if (initialized) {
        return;
      }
      setInitialized(true);

      if (membershipStatus === 'not_member') {
        if (userId) {
          naptime
            .executeMutation(
              ProgramSwitcherSelectionsV1.update(userId, {
                selectionType: 'COURSERA',
              })
            )
            .then(() => {
              redirect.setLocation(`/degrees/${degreeSlug}`);
            });
        } else {
          redirect.setLocation(`/degrees/${degreeSlug}`);
        }
      } else if (membershipStatus === 'member') {
        naptime.executeMutation(
          ProgramSwitcherSelectionsV1.update(userId, {
            selectionType: 'DEGREE',
            degreeId: degreeMetadata.id,
          })
        );
      }

      initializePendo(degreeMetadata, degreeSlug);
    }, // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [membershipStatus, initialized]
  );

  if (membershipStatus !== 'member') {
    return null;
  }

  const { id, supportEmailAddress } = degreeMetadata;
  const isMasterTrack = stringKeyToTuple(id)[0] === 'credentialTrack';
  const shouldShowPrivacyWarningBanner = shouldShowToUBanner();

  return (
    <DegreeContextProvider degreeId={id}>
      <CoursesContextProvider degreeId={id}>
        <TrackedDiv trackingName="degree_home_app" className="rc-DegreeHomeApp" withVisibilityTracking>
          <CourseraMetatags
            metaNameAndProperties={{
              title: metatagTitle,
            }}
          />
          <UniversalPageFeatures />
          <DegreePageHeader />
          {shouldShowPrivacyWarningBanner && <PrivacyWarningBanner />}
          <DegreeHomeHeader
            degreeMetadata={degreeMetadata}
            pageContent={pageContent}
            partners={partners}
            userAgent={userAgent}
          />
          <TimezoneMismatchNotification />
          <EnrollmentDialogContainer />
          <TabsContainer isDPMEnabled={isDPMEnabled}>
            {children &&
              React.cloneElement(children, {
                supportEmailAddress,
                ...props,
              })}
          </TabsContainer>
          <IntroModalWrapper isMasterTrack={isMasterTrack} />
        </TrackedDiv>
      </CoursesContextProvider>
    </DegreeContextProvider>
  );
};

export { DegreeHomeAppV2 as BaseComp };

export default flowRight(
  mapProps<{ metatagTitle: string }, { location: { pathname: string }; degreeMetadata: { name: string } }>((props) => {
    const { location, degreeMetadata } = props;

    const name = degreeMetadata?.name;

    const isSummary = location.pathname.indexOf('summary') !== -1;
    let metatagTitle = `${name} | Home`;

    if (isSummary) {
      metatagTitle = `${name} | Summary`;
    }

    return { metatagTitle };
  }),
  connectToRouter(({ location }) => {
    const previewParam = location.query.preview;
    const isPreview = !!previewParam && Number(previewParam) === 1;

    return {
      isPreview,
    };
  }),
  connectToStores(['ApplicationStore'], ({ ApplicationStore }) => {
    return {
      userAgent: ApplicationStore.getUserAgent(),
    };
  }),
  graphql<
    { isPreview: boolean; degreeSlug: string },
    DegreeHomePageContentQuery,
    DegreeHomePageContentQueryVariables,
    { pageContent?: PageContent | null }
  >(degreeHomePageContentQuery, {
    options: ({ isPreview, degreeSlug }) => {
      return {
        variables: {
          preview: !!isPreview,
          slug: degreeSlug,
          locale: getCmsLocaleString(),
        },
        context: getGrowthCmsGraphqlContext(isPreview),
      };
    },
    props: ({ data }) => {
      const universityProgramPageContent = data?.universityProgramPageContentCollection?.items[0];
      const premiumCertificatePageContent = data?.premiumCertificatePageContentCollection?.items[0];
      return {
        pageContent:
          (universityProgramPageContent &&
            convertUniversityProgramPageContentToGenericPageContent(universityProgramPageContent)) ||
          (premiumCertificatePageContent &&
            convertPremiumCertificatePageContentToGenericPageContent(premiumCertificatePageContent)),
      };
    },
  }),
  withNaptimeAsProp,
  withMarketingConsent,
  Retracked.createTrackedContainer<Props>(({ degreeSlug, isDPMEnabled }) => {
    return {
      namespace: {
        app: 'degree_home',
        page: isDPMEnabled ? 'degree_home_v3_page' : 'degree_home_v2_page',
      },
      degreeContext: `degree|${degreeSlug}`,
      browserWindowId: browserTabOrWindowUniqueIdentifier(),
    };
  })
)(DegreeHomeAppV2);
